import { RenderWidgets } from '~/components/widget/RenderWidgets';
import { AppRoute } from '@agentselly/immodossier-shared';
import type { ReactNode } from 'react';
import {
  createFileRoute,
  useCanGoBack,
  useRouter,
} from '@tanstack/react-router';
import { useTranslate } from '@tolgee/react';
import { ActionButton } from '~/components/button/ActionButton';
import { clsx } from 'clsx';
import { useLoaderQuery } from '~/helper/useLoaderQuery';
import { LoadingWidget } from '~/components/widget/base/LoadingWidget';
import { ErrorWidget } from '~/components/widget/base/ErrorWidget';
import { useAppParams } from '~/helper/useAppParams';
import { makeDynamicConfigWidgets } from '~/helper/makeDynamicConfigWidgets';

export const Route = createFileRoute(
  AppRoute.$LANG_$COMPANYID_PARTNER_$EXTERNALREFERENCE_RESULT,
)({
  component: Body,
  staleTime: 0,
});

function Body() {
  const { t } = useTranslate();

  const params = useAppParams(Route);

  const firstIntervalRequest = Date.now();

  const query = useLoaderQuery(
    AppRoute.$LANG_$COMPANYID_PARTNER_$EXTERNALREFERENCE_RESULT,
    {
      refetchInterval: (context: any) => {
        const widgetProps = context?.state?.data?.widgetProps;

        if (widgetProps && Object.keys(widgetProps).length) {
          return false;
        }

        const diff = Date.now() - firstIntervalRequest;

        if (diff <= 10000) {
          // Retry every 1 second for the first 10 seconds
          return 1000;
        }

        if (diff <= 30000) {
          // Retry every 5 seconds for the next 20 seconds
          return 5000;
        }

        // Retry every 60 seconds after 30 seconds
        return 60000;
      },
      params: params,
    },
  );

  if (query.isLoading) {
    return <LoadingWidget />;
  }

  if (query.isError || !query.data) {
    return <ErrorWidget error={query.error} />;
  }

  if (query.data.error) {
    switch (query.data.error) {
      case 'already_known':
      case 'dossier_exists':
        return (
          <ErrorCard
            title={t('calculator_result_page_error_title')}
            link="/"
            linkLabel={t(
              'calculator_result_page_already_known_error_message_link_text',
            )}
          >
            <p>
              {t(
                'calculator_result_page_already_known_error_message_prefix_text',
              )}{' '}
              <a
                href="/"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                {t(
                  'calculator_result_page_already_known_error_message_link_text',
                )}
              </a>{' '}
              {t(
                'calculator_result_page_already_known_error_message_postfix_text',
              )}
            </p>
          </ErrorCard>
        );
      case 'invalid_email':
        return (
          <ErrorCard
            title={t('calculator_result_page_error_title')}
            fallbackLink="/"
            linkLabel={t('calculator_result_page_back_link')}
          >
            <p>{t('calculator_result_page_invalid_email_error_message')}</p>
          </ErrorCard>
        );
      case 'invalid_address':
        return (
          <ErrorCard
            title={t('calculator_result_page_error_title')}
            fallbackLink="/"
            linkLabel={t('calculator_result_page_back_link')}
          >
            <p className="whitespace-pre-wrap">
              {t('calculator_result_page_invalid_address_error_message', {
                address: query.data.errorInputFullAddress || '',
              })}
            </p>
          </ErrorCard>
        );
      default:
        return (
          <ErrorCard
            title={t('calculator_result_page_error_title')}
            link="/"
            linkLabel={t('calculator_result_page_login_link')}
          >
            <p>{t('calculator_result_page_unknown_error_message')}</p>
            <p className="my-2">
              {t('calculator_result_page_unknown_session_id', {
                value: params.sessionId || 'error',
              })}
            </p>
            <p>
              <a
                href="mailto:support@agentselly.ch"
                className="underline text-blue-600"
              >
                support@agentselly.ch
              </a>
            </p>
          </ErrorCard>
        );
    }
  }

  return (
    <RenderWidgets
      showLoadingMessage={Object.keys(query.data.widgetProps).length === 0}
      widgets={widgets(query.data.widgetProps)}
      sessionId={params.sessionId}
    />
  );
}

function ErrorCard(props: ErrorCardProps) {
  return (
    <div
      className={clsx(
        'h-full drop-shadow-md rounded-xl block p-3 md:p-8 max-w-[500px]',
        props.white
          ? 'bg-white dark:bg-gray-800'
          : 'bg-red-50 border-2 border-red-600',
      )}
    >
      <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-300 dark:text-gray-200 mb-2">
        {props.title}
      </h1>
      {props.children}
      {props.link ? (
        <div className="mt-4">
          <a href={props.link}>
            <ActionButton
              type="button"
              primary
            >
              {props.linkLabel || 'ERROR'}
            </ActionButton>
          </a>
        </div>
      ) : props.fallbackLink ? (
        <BackButtonRow
          linkLabel={props.linkLabel}
          fallbackLink={props.fallbackLink}
        />
      ) : null}
    </div>
  );
}

function BackButtonRow(props: {
  readonly linkLabel?: string;
  readonly fallbackLink: string;
}) {
  const router = useRouter();
  const canGoBack = useCanGoBack();

  return (
    <div className="mt-4">
      <ActionButton
        type="button"
        primary
        onClick={() => {
          if (canGoBack) {
            router.history.back();
          } else {
            void router.navigate({ to: props.fallbackLink });
          }
        }}
      >
        {props.linkLabel || 'ERROR'}
      </ActionButton>
    </div>
  );
}

interface ErrorCardProps {
  readonly title: string;
  readonly children: ReactNode;
  readonly link?: string;
  readonly linkLabel?: string;
  readonly fallbackLink?: string;
  readonly onBackClick?: () => void;
  readonly white?: boolean;
}

const widgets = makeDynamicConfigWidgets([
  {
    name: 'partnerNavigation',
    cols: 6,
    props: {
      items: [
        {
          link: '#evaluation',
          label: 'partner_nav_evaluation',
        },
        {
          link: '#market',
          label: 'partner_nav_market',
        },
        {
          link: '#energy',
          label: 'partner_nav_energy',
        },
        {
          link: '#content',
          label: 'partner_nav_content',
        },
      ],
    },
  },
  {
    name: 'partnerWelcome',
    cols: 6,
  },
  {
    name: 'partnerSection',
    cols: 6,
    props: {
      id: 'evaluation',
      label: 'partner_nav_evaluation',
    },
  },
  {
    name: 'partnerEvaluationValue',
    cols: 6,
  },
  {
    name: 'partnerEvaluationMap',
    cols: 6,
  },
  {
    name: 'partnerSection',
    cols: 6,
    props: {
      id: 'market',
      label: 'partner_nav_market',
    },
  },
  {
    name: 'partnerMarketDemand',
    cols: 6,
  },
  {
    name: 'partnerMarketOffer',
    cols: 6,
  },
  {
    name: 'partnerSection',
    cols: 6,
    props: {
      id: 'energy',
      label: 'partner_nav_energy',
    },
  },
  {
    name: 'partnerEnergyCost',
    cols: 6,
  },
  {
    name: 'partnerEnergyDemand',
    cols: 6,
  },
  {
    name: 'partnerEnergyEmissions',
    cols: 6,
  },
  {
    name: 'partnerSection',
    cols: 6,
    props: {
      id: 'content',
      label: 'partner_nav_content',
    },
  },
  {
    name: 'partnerFrequentlyAskedQuestions',
    cols: 6,
  },
  {
    name: 'partnerContent',
    cols: 6,
  },
  {
    name: 'partnerConsulting',
    cols: 6,
  },
]);
